import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listingsAPI, subscriptionsAPI, authAPI } from '../utils/api';
import { Package, Plus, Eye, Trash2, Pencil, AlertTriangle, Bell, BellOff, Sparkles, Rocket, Zap, RotateCcw, CheckCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import ResponsiveImage from '../components/ResponsiveImage';
import AddonPurchaseModal from '../components/AddonPurchaseModal';
import VerifiedUserBadge from '../components/VerifiedUserBadge';
import { isIosNativeApp } from '../utils/applePay';

const UPGRADE_ADDONS = {
  pro_listing:    { addon_id: 'pro_listing',    name: 'Pro Listing',      price: 25, kind: 'pro' },
  boost_entry:    { addon_id: 'boost_entry',    name: 'Boost (Entry)',    price: 9,  kind: 'boost' },
  boost_standard: { addon_id: 'boost_standard', name: 'Boost (Standard)', price: 19, kind: 'boost' },
};

const Dashboard = () => {
  const [myListings, setMyListings] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expiryOptOut, setExpiryOptOut] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [upgradeTarget, setUpgradeTarget] = useState(null); // { addon, listing_id }
  // Iter52: tap-toggleable tooltip for the Restore "i" button — same UX as Subscriptions add-on tooltips.
  const [openRestoreInfo, setOpenRestoreInfo] = useState(''); // listing_id or ''
  const restoreInfoRef = useRef(null);
  const { user, loading: authLoading, checkAuth } = useAuth();
  const navigate = useNavigate();

  // Close tooltip on outside click
  useEffect(() => {
    if (!openRestoreInfo) return;
    const onClick = (e) => {
      if (restoreInfoRef.current && !restoreInfoRef.current.contains(e.target)) setOpenRestoreInfo('');
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [openRestoreInfo]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    setExpiryOptOut(!!user.expiry_email_opt_out);
    loadDashboardData();
  }, [user, authLoading, navigate]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [listingsRes, subsRes] = await Promise.all([
        listingsAPI.getMy(),
        subscriptionsAPI.getMy()
      ]);
      
      setMyListings(listingsRes.data);
      setSubscriptions(subsRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    
    try {
      await listingsAPI.delete(listingId);
      toast.success('Listing deleted');
      loadDashboardData();
    } catch (error) {
      console.error('Failed to delete listing:', error);
      toast.error('Failed to delete listing');
    }
  };

  const handleMarkSold = async (listing) => {
    if (!window.confirm(`Mark "${listing.title}" as sold? It will be removed from buyers' search results but kept in your account history.`)) return;
    try {
      await listingsAPI.markSold(listing.listing_id);
      toast.success('Marked as sold!');
      loadDashboardData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark as sold');
    }
  };

  const handleReactivate = async (listing) => {
    const purged = listing.images_purge_state === 'purged';
    const msg = purged
      ? 'Images for this listing were permanently deleted. Restoring will use 1 ad slot from your active plan, and you will need to upload fresh photos. Continue?'
      : 'Restore this listing so it goes live again? Your archived images will return automatically (free of cost).';
    if (!window.confirm(msg)) return;
    try {
      const res = await listingsAPI.reactivate(listing.listing_id);
      toast.success(purged ? 'Listing restored — please add new photos' : 'Listing is live again!');
      if (res.data.needs_reupload) {
        navigate(`/edit-listing/${listing.listing_id}`);
      } else {
        loadDashboardData();
      }
    } catch (error) {
      const code = error?.response?.status;
      if (code === 402) {
        // iter124 — Surface backend's precise reason (e.g. dealer-category mismatch)
        // so the user understands why they need to pick a plan.
        const detail = error?.response?.data?.detail;
        toast.message(
          typeof detail === 'string' && detail
            ? detail
            : 'Pick a plan to restore — your ad will be restored automatically after payment.',
          { duration: 5000 }
        );
        // Pass restore target so Subscriptions can return to this listing on success
        navigate(`/subscriptions?restore_listing=${listing.listing_id}`);
      } else {
        toast.error(error?.response?.data?.detail || 'Failed to restore');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-[#1800ad] mx-auto mb-4"></div>
          <p className="text-[#4B5563] font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  const activeSubs = subscriptions.filter(s => s.is_active);
  const totalAdsRemaining = activeSubs.reduce((sum, s) => sum + s.ads_remaining, 0);

  const getDaysUntil = (dateStr) => {
    const now = new Date();
    const then = new Date(dateStr);
    return Math.ceil((then - now) / (1000 * 60 * 60 * 24));
  };
  const expiringSoon = activeSubs
    // Skip lifetime grants and any sub without an expiry date — these should
    // never show "expires in X days" warnings.
    .filter((s) => !s.is_lifetime && s.expires_at)
    .map(s => ({ sub: s, days: getDaysUntil(s.expires_at) }))
    .filter(x => x.days >= 0 && x.days <= 3)
    .sort((a, b) => a.days - b.days);

  const handleToggleExpiryEmails = async () => {
    setSavingPrefs(true);
    const next = !expiryOptOut;
    try {
      await authAPI.updateEmailPreferences(next);
      setExpiryOptOut(next);
      toast.success(next ? 'Expiry email reminders disabled' : 'Expiry email reminders enabled');
      if (checkAuth) checkAuth();
    } catch (_) {
      toast.error('Could not update preference');
    } finally {
      setSavingPrefs(false);
    }
  };

  // Boost lifecycle helper: hours left + the matching add-on to renew with.
  const boostInfo = (listing) => {
    if (!listing.is_boosted || !listing.boost_expires_at) return null;
    const hrs = (new Date(listing.boost_expires_at) - new Date()) / 3600000;
    const tier = (listing.boost_type || '').toLowerCase() === 'entry' ? 'boost_entry' : 'boost_standard';
    let addon = UPGRADE_ADDONS[tier];
    // Boost (Entry) ₹9 isn't sold on iOS (below Apple's IAP minimum) — fall back to Standard.
    if (isIosNativeApp && addon.addon_id === 'boost_entry') addon = UPGRADE_ADDONS.boost_standard;
    return { hrs, addon };
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-4 sm:py-8">
      <div className="max-w-7xl mx-auto px-3 sm:px-6">
        <div className="mb-6 sm:mb-8">
          <Link to="/" className="text-[#4B5563] hover:text-[#1800ad] font-medium mb-3 inline-block text-xs sm:text-base">&larr; Back to Home</Link>
          <h1 className="text-2xl sm:text-4xl font-black text-[#0A0B10] mb-1 sm:mb-2 flex items-center gap-2 flex-wrap" style={{fontFamily: 'Google Sans Flex'}} data-testid="dashboard-title">
            My Dashboard
            {user?.phone_verified && <VerifiedUserBadge size="md" label="Verified" />}
          </h1>
          <p className="text-sm sm:text-base text-[#4B5563]">Welcome back, {user.name}!</p>
        </div>

        {/* iter89 — Trusted Seller upsell — only for verified users who don't
            already have Trusted Seller / dealer status. Encourages upgrade
            without being pushy. */}
        {user?.phone_verified && !user?.trusted_seller && !user?.is_dealer && (
          <div
            data-testid="trusted-seller-upsell"
            className="mb-5 sm:mb-6 rounded-2xl bg-gradient-to-br from-[#fff4ed] to-[#f0edff] border border-[#ff8500]/25 p-3 sm:p-4 flex items-center gap-3 shadow-sm"
          >
            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-[#ff8500] to-[#ff6b6b] flex items-center justify-center">
              <Sparkles className="text-white" size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm sm:text-base font-bold text-[#0A0B10]">Level up to <span className="text-[#ff8500]">Trusted Seller</span></p>
              <p className="text-[11px] sm:text-xs text-[#6B7280] leading-snug mt-0.5">Unlock the ⭐ Trusted Seller badge, priority placement and 3× more inquiries with an eligible subscription plan.</p>
            </div>
            <Link
              to="/subscriptions"
              data-testid="trusted-seller-upgrade-btn"
              className="flex-shrink-0 bg-[#1800ad] text-white text-xs sm:text-sm font-bold rounded-full px-3 sm:px-4 py-1.5 sm:py-2 hover:bg-[#1200a0] transition-colors whitespace-nowrap"
            >
              View Plans
            </Link>
          </div>
        )}

        {/* Plan Expiry Banner */}
        {expiringSoon.length > 0 && (
          <div
            data-testid="plan-expiry-banner"
            className="mb-5 sm:mb-6 rounded-2xl border-2 border-red-400 bg-gradient-to-r from-red-50 to-[#fff4ed] p-3 sm:p-4 flex items-start sm:items-center gap-3 shadow-sm"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
              <AlertTriangle className="text-white" size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm sm:text-base text-red-700">
                {expiringSoon[0].days === 0
                  ? 'Your plan expires today!'
                  : `Your plan expires in ${expiringSoon[0].days} day${expiringSoon[0].days !== 1 ? 's' : ''}`}
              </p>
              <p className="text-xs sm:text-sm text-red-600/80 mt-0.5">
                {expiringSoon[0].sub.plan_type.replace('_', ' ').toUpperCase()} — renew now to keep posting ads without interruption.
              </p>
            </div>
            <Link
              to="/subscriptions"
              data-testid="expiry-banner-renew-btn"
              className="flex-shrink-0 bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm font-bold rounded-full px-4 py-2 transition-colors whitespace-nowrap"
            >
              Renew Now
            </Link>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-2 sm:gap-6 mb-6 sm:mb-8" data-testid="stats-section">
          <div className="bg-white rounded-xl sm:rounded-3xl p-3 sm:p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-2 sm:mb-4">
              <h3 className="text-[10px] sm:text-lg font-bold text-[#0A0B10]">Active</h3>
              <Package className="text-[#1800ad] hidden sm:block" size={24} />
            </div>
            <p className="text-xl sm:text-4xl font-black text-[#1800ad]" style={{fontFamily: 'Google Sans Flex'}}>
              {myListings.filter(l => l.status === 'active').length}
            </p>
          </div>

          <div className="bg-white rounded-xl sm:rounded-3xl p-3 sm:p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-2 sm:mb-4">
              <h3 className="text-[10px] sm:text-lg font-bold text-[#0A0B10]">Views</h3>
              <Eye className="text-[#ff8500] hidden sm:block" size={24} />
            </div>
            <p className="text-xl sm:text-4xl font-black text-[#ff8500]" style={{fontFamily: 'Google Sans Flex'}}>
              {myListings.reduce((sum, l) => sum + l.views, 0)}
            </p>
          </div>

          <div className="bg-white rounded-xl sm:rounded-3xl p-3 sm:p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-2 sm:mb-4">
              <h3 className="text-[10px] sm:text-lg font-bold text-[#0A0B10]">Ads Left</h3>
              <Plus className="text-[#10B981] hidden sm:block" size={24} />
            </div>
            <p className="text-xl sm:text-4xl font-black text-[#10B981]" style={{fontFamily: 'Google Sans Flex'}}>
              {totalAdsRemaining}
            </p>
          </div>
        </div>

        {/* Active Subscriptions */}
        {activeSubs.length > 0 && (
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 mb-8" data-testid="subscriptions-section">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <h2 className="text-2xl font-bold text-[#0A0B10]" style={{fontFamily: 'Google Sans Flex'}}>Active Subscriptions</h2>
              <button
                data-testid="toggle-expiry-emails-btn"
                onClick={handleToggleExpiryEmails}
                disabled={savingPrefs}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors disabled:opacity-60 ${expiryOptOut ? 'bg-gray-100 text-[#6B7280] border-gray-200 hover:bg-gray-200' : 'bg-[#f0edff] text-[#1800ad] border-[#1800ad]/20 hover:bg-[#1800ad]/10'}`}
                title={expiryOptOut ? 'Expiry reminder emails are OFF — click to turn ON' : 'Expiry reminder emails are ON — click to turn OFF'}
              >
                {expiryOptOut ? <BellOff size={12} /> : <Bell size={12} />}
                {expiryOptOut ? 'Expiry emails: Off' : 'Expiry emails: On'}
              </button>
            </div>
            <div className="space-y-3">
              {activeSubs.map((sub) => (
                <div key={sub.subscription_id} className="flex items-center justify-between p-4 bg-[#f0edff] rounded-2xl">
                  <div>
                    <p className="font-bold text-[#0A0B10]">{sub.plan_type.replace('_', ' ').toUpperCase()}</p>
                    <p className="text-sm text-[#4B5563]">Ads: {sub.ads_remaining} | Boosts: {sub.boosts_remaining}</p>
                  </div>
                  <p className="text-sm text-[#9CA3AF]">
                    Expires: {new Date(sub.expires_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-2 sm:gap-4 mb-6 sm:mb-8" data-testid="actions-section">
          <Link
            to="/post-ad"
            data-testid="post-ad-link"
            className="bg-[#1800ad] text-white rounded-full px-5 py-2.5 sm:px-8 sm:py-3 text-sm font-bold hover:bg-[#1200a0] transition-colors shadow-lg shadow-[#1800ad]/20 flex items-center gap-2"
          >
            <Plus size={18} /> Post New Ad
          </Link>
          
          {totalAdsRemaining === 0 && (
            <Link
              to="/subscriptions"
              data-testid="buy-plan-link"
              className="bg-[#ff8500] text-white rounded-full px-5 py-2.5 sm:px-8 sm:py-3 text-sm font-bold hover:bg-[#e57500] transition-colors shadow-lg shadow-[#ff8500]/20"
            >
              Buy More Ads
            </Link>
          )}
        </div>

        {/* My Listings */}
        <div className="bg-white rounded-xl sm:rounded-3xl p-3 sm:p-6 shadow-sm border border-gray-100" data-testid="listings-section">
          <h2 className="text-lg sm:text-2xl font-bold text-[#0A0B10] mb-4 sm:mb-6" style={{fontFamily: 'Google Sans Flex'}}>My Listings</h2>
          
          {myListings.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <p className="text-[#9CA3AF] mb-4 text-sm">No listings yet</p>
              <Link
                to="/post-ad"
                className="inline-block bg-[#1800ad] text-white rounded-full px-6 py-2.5 sm:px-8 sm:py-3 text-sm font-bold hover:bg-[#1200a0] transition-colors"
              >
                Create Your First Listing
              </Link>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {myListings.map((listing) => (
                <div
                  key={listing.listing_id}
                  data-testid={`my-listing-${listing.listing_id}`}
                  className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 border border-gray-200 rounded-xl sm:rounded-2xl hover:border-[#1800ad] transition-colors"
                >
                  <div className="flex items-center gap-3 sm:gap-4">
                    {listing.images && listing.images.length > 0 ? (
                      <ResponsiveImage
                        path={listing.images[0]}
                        alt={listing.title}
                        widths={[256, 512]}
                        sizes="96px"
                        className="w-16 h-16 sm:w-24 sm:h-24 object-cover rounded-lg sm:rounded-xl flex-shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-gray-400 text-[9px] sm:text-xs">No Image</span>
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-[#0A0B10] text-sm sm:text-base mb-0.5 line-clamp-1">{listing.title}</h3>
                      <p className="text-base sm:text-lg font-black text-[#1800ad]" style={{fontFamily: 'Google Sans Flex'}}>₹{listing.price.toLocaleString()}</p>
                      <div className="flex gap-1.5 mt-1.5">
                        <span className={`text-[10px] sm:text-xs px-2 sm:px-3 py-0.5 sm:py-1 rounded-full font-bold ${
                          listing.status === 'active' ? 'bg-green-100 text-green-700 border border-green-200' : 
                          listing.status === 'pending_review' ? 'bg-amber-100 text-amber-700 border border-amber-200' :
                          listing.status === 'rejected' ? 'bg-red-100 text-red-700 border border-red-200' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {listing.status === 'pending_review' ? 'REVIEW' : listing.status.toUpperCase()}
                        </span>
                        <span className="text-[10px] sm:text-xs px-2 sm:px-3 py-0.5 sm:py-1 rounded-full bg-[#f0edff] text-[#1800ad] font-bold">
                          {listing.views} views
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2 sm:ml-auto sm:flex-shrink-0">
                    {/* Restore options for expired / archived / purged listings */}
                    {(listing.status === 'expired' || listing.images_purge_state === 'archived' || listing.images_purge_state === 'purged') && (
                      <div className="flex flex-wrap gap-2 justify-center sm:justify-end items-center" data-testid={`restore-options-${listing.listing_id}`}>
                        <button
                          data-testid={`restore-listing-${listing.listing_id}`}
                          onClick={() => handleReactivate(listing)}
                          className="px-3 py-1.5 rounded-full bg-gradient-to-r from-[#1800ad] to-[#ff8500] text-white hover:opacity-90 transition-opacity flex items-center gap-1.5 text-[11px] sm:text-xs font-bold"
                          title="Restore this ad by purchasing a plan or choosing an eligible subscription. Your archived ad details and images will be restored automatically."
                        >
                          <RotateCcw size={12} /> Restore
                        </button>
                        <div className="relative" ref={openRestoreInfo === listing.listing_id ? restoreInfoRef : null}>
                          <button
                            type="button"
                            data-testid={`restore-info-${listing.listing_id}`}
                            aria-label="About Restore"
                            aria-expanded={openRestoreInfo === listing.listing_id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenRestoreInfo(openRestoreInfo === listing.listing_id ? '' : listing.listing_id);
                            }}
                            className="w-7 h-7 rounded-full bg-[#f0edff] text-[#1800ad] text-xs font-bold flex items-center justify-center cursor-pointer hover:bg-[#1800ad] hover:text-white transition-colors active:scale-95"
                          >i</button>
                          {openRestoreInfo === listing.listing_id && (
                            <div
                              role="tooltip"
                              data-testid={`restore-tooltip-${listing.listing_id}`}
                              className="absolute right-0 top-9 w-64 max-w-[80vw] z-30 bg-[#0A0B10] text-white text-xs leading-relaxed rounded-xl p-3 shadow-2xl"
                            >
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <p className="font-bold text-[#ff8500] text-sm">About Restore</p>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setOpenRestoreInfo(''); }}
                                  className="text-white/70 hover:text-white -mr-1 -mt-1 p-0.5"
                                  aria-label="Close"
                                  data-testid={`restore-tooltip-close-${listing.listing_id}`}
                                >
                                  <X size={14} />
                                </button>
                              </div>
                              <p className="whitespace-pre-line">
                                {`Restore brings an expired ad back to life.

• Requires an active plan
• Images are restored automatically if your ad was restored before the image purge window begins (free, no ad slot consumed)
• After image purge, restore is still possible but you must re-upload images and 1 ad slot is consumed from your plan`}
                              </p>
                              <span className="absolute -top-1.5 right-3 w-3 h-3 bg-[#0A0B10] rotate-45"></span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Upgrade options (Pro / Boost) — only on active listings */}
                    {listing.status === 'active' && (
                      <div className="flex flex-wrap gap-2 justify-center sm:justify-end" data-testid={`upgrade-options-${listing.listing_id}`}>
                        <button
                          data-testid={`mark-sold-${listing.listing_id}`}
                          onClick={() => handleMarkSold(listing)}
                          className="px-3 py-1.5 rounded-full bg-[#00bf63] text-white hover:bg-[#00a554] transition-colors flex items-center gap-1.5 text-[11px] sm:text-xs font-bold"
                          title="Mark this listing as sold — it will hide from buyers' search results but stay in your account history"
                        >
                          <CheckCircle size={12} /> Mark Sold
                        </button>
                        {!listing.is_pro && (
                          <button
                            data-testid={`upgrade-pro-${listing.listing_id}`}
                            onClick={() => setUpgradeTarget({ addon: UPGRADE_ADDONS.pro_listing, listing_id: listing.listing_id })}
                            className="px-3 py-1.5 rounded-full bg-gradient-to-r from-[#1800ad] to-[#1200a0] text-white hover:opacity-90 transition-opacity flex items-center gap-1.5 text-[11px] sm:text-xs font-bold"
                            title="Convert to Pro Listing"
                          >
                            <Sparkles size={12} /> Make Pro · ₹25
                          </button>
                        )}
                        {!listing.is_boosted && (
                          <>
                            {!isIosNativeApp && (
                              <button
                                data-testid={`upgrade-boost-entry-${listing.listing_id}`}
                                onClick={() => setUpgradeTarget({ addon: UPGRADE_ADDONS.boost_entry, listing_id: listing.listing_id })}
                                className="px-3 py-1.5 rounded-full bg-[#fff4ed] text-[#ff8500] hover:bg-[#ff8500] hover:text-white border border-[#ff8500]/30 transition-colors flex items-center gap-1.5 text-[11px] sm:text-xs font-bold"
                                title="Boost (Entry) — 3-day boost"
                              >
                                <Zap size={12} /> Boost 3-day · ₹9
                              </button>
                            )}
                            <button
                              data-testid={`upgrade-boost-standard-${listing.listing_id}`}
                              onClick={() => setUpgradeTarget({ addon: UPGRADE_ADDONS.boost_standard, listing_id: listing.listing_id })}
                              className="px-3 py-1.5 rounded-full bg-[#ff8500] text-white hover:bg-[#E67520] transition-colors flex items-center gap-1.5 text-[11px] sm:text-xs font-bold"
                              title="Boost (Standard) — 7-day boost"
                            >
                              <Rocket size={12} /> Boost 7-day · ₹19
                            </button>
                          </>
                        )}
                        {listing.is_boosted && (() => {
                          const info = boostInfo(listing);
                          if (!info) {
                            return (
                              <span
                                data-testid={`boost-status-${listing.listing_id}`}
                                className="px-3 py-1.5 rounded-full bg-[#fff4ed] text-[#ff8500] border border-[#ff8500]/30 flex items-center gap-1.5 text-[11px] sm:text-xs font-bold"
                              >
                                <Zap size={12} /> Boosted
                              </span>
                            );
                          }
                          const { hrs, addon } = info;
                          if (hrs <= 24) {
                            const label = hrs <= 0 ? 'ending now' : (hrs < 1 ? 'ends in <1h' : `ends in ${Math.ceil(hrs)}h`);
                            return (
                              <button
                                data-testid={`renew-boost-${listing.listing_id}`}
                                onClick={() => setUpgradeTarget({ addon, listing_id: listing.listing_id })}
                                className="px-3 py-1.5 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-1.5 text-[11px] sm:text-xs font-bold animate-pulse"
                                title={`Your boost ${label}. Renew to keep priority placement.`}
                              >
                                <Zap size={12} /> Boost {label} — Renew ₹{addon.price}
                              </button>
                            );
                          }
                          const days = Math.max(1, Math.round(hrs / 24));
                          return (
                            <span
                              data-testid={`boost-status-${listing.listing_id}`}
                              className="px-3 py-1.5 rounded-full bg-[#fff4ed] text-[#ff8500] border border-[#ff8500]/30 flex items-center gap-1.5 text-[11px] sm:text-xs font-bold"
                              title="This listing has an active boost"
                            >
                              <Zap size={12} /> Boosted · {days}d left
                            </span>
                          );
                        })()}
                      </div>
                    )}
                    <div className="flex gap-2 justify-center sm:justify-end flex-shrink-0">
                      <Link
                        to={`/edit-listing/${listing.listing_id}`}
                        data-testid={`edit-listing-${listing.listing_id}`}
                        className="px-3 py-2 rounded-xl bg-[#fff4ed] text-[#ff8500] hover:bg-[#ff8500] hover:text-white transition-colors flex items-center gap-1.5 text-xs font-bold"
                      >
                        <Pencil size={14} /> Edit
                      </Link>
                      <Link
                        to={`/listing/${listing.listing_id}`}
                        data-testid={`view-listing-${listing.listing_id}`}
                        className="px-3 py-2 rounded-xl bg-[#f0edff] text-[#1800ad] hover:bg-[#1800ad] hover:text-white transition-colors flex items-center gap-1.5 text-xs font-bold"
                      >
                        <Eye size={14} /> View
                      </Link>
                      <button
                        data-testid={`delete-listing-${listing.listing_id}`}
                        onClick={() => handleDeleteListing(listing.listing_id)}
                        className="px-3 py-2 rounded-xl bg-red-50 text-red-600 hover:bg-red-600 hover:text-white transition-colors flex items-center gap-1.5 text-xs font-bold"
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <AddonPurchaseModal
        open={!!upgradeTarget}
        addon={upgradeTarget?.addon}
        preselectedListingId={upgradeTarget?.listing_id}
        onClose={() => setUpgradeTarget(null)}
        onSuccess={() => loadDashboardData()}
      />
    </div>
  );
};

export default Dashboard;